import React, { useMemo } from 'react';
import { VendorCTAType } from '../../types';
import { VendorGroupContainer } from './VendorCTAsGroup.style';
import VendorCtaSelector from '../VendorCtaSelector/VendorCtaSelector';
import { StockCarType } from '../../types/oneGraphTypes';

interface VendorCTAsProps {
  ctas: VendorCTAType[];
  vin: string;
  vehicleId?: string;
  market: string;
  mainDealer?: string;
  cartypeText: string;
  vehicleData: StockCarType;
}

const VendorCTAsGroup: React.FC<VendorCTAsProps> = ({
  ctas,
  vin,
  vehicleId,
  market,
  mainDealer,
  cartypeText,
  vehicleData,
}) => {
  const {
    dynamicAttributes,
    carline,
    modelInfo,
    mileage,
    model,
    subtitleText,
    carPrices,
    qualityLabel,
  } = vehicleData;

  const isNonAudi = useMemo(() => {
    if (modelInfo?.genericModel?.code?.substring(0, 2) === 'AA') return false;
    return true;
  }, [modelInfo?.genericModel?.code]);

  // Mileage
  const modelMileage = useMemo(() => {
    return mileage?.value?.number;
  }, [mileage?.value?.number]);

  // Model Code
  const modelCode = useMemo(() => {
    if (isNonAudi) {
      return modelInfo?.genericModel?.code;
    }
    return model?.id?.code;
  }, [isNonAudi, model?.id?.code, modelInfo?.genericModel?.code]);

  // Make(code) - like AA (means Audi)
  const modelMake = useMemo(() => {
    const makeCode = modelInfo?.genericModel?.code?.substring(0, 2);
    return makeCode === 'AA' ? 'Audi' : makeCode;
  }, [modelInfo?.genericModel?.code]);

  // Trim Name
  const trimName = useMemo(() => {
    if (subtitleText?.length) return subtitleText;
    return modelInfo?.genericModel?.text;
    return subtitleText;
  }, [modelInfo?.genericModel?.text, subtitleText]);

  // Model Year
  const modelYear = useMemo(() => {
    if (isNonAudi) {
      return modelInfo?.modelyear;
    }
    return model?.salesModelyear;
  }, [isNonAudi, model?.salesModelyear, modelInfo?.modelyear]);

  // Model Price Retail(sale)
  const modelPriceRetail = useMemo(() => {
    const retailPrice = carPrices?.find((item) => item?.type === 'sale');
    if (retailPrice) {
      return retailPrice?.price?.value;
    }
    return null;
  }, [carPrices]);

  // Model Price Regular(list)
  const listPrice = useMemo(() => {
    const regularPrice = carPrices?.find((item) => item?.type === 'list');
    if (regularPrice) {
      return regularPrice?.price?.value;
    }
    return null;
  }, [carPrices]);

  // Model Price Regular(list)
  const dealerPrice = useMemo(() => {
    const priceDealer = carPrices?.find((item) => item?.type === 'dealerPrice');
    if (priceDealer) {
      return priceDealer?.price?.value;
    }
    return null;
  }, [carPrices]);

  // Model ID
  const modelID = useMemo(() => {
    if (isNonAudi) {
      return null;
    }
    return carline?.id;
  }, [carline?.id, isNonAudi]);

  // Stock Number
  const stockNumber = useMemo(() => {
    const stockNumberObject = dynamicAttributes?.find((key) => key?.id === 'VEHICLE_ID');
    if (stockNumberObject) {
      return stockNumberObject?.value;
    }
    return null;
  }, [dynamicAttributes]);

  const isNewVehicle = cartypeText === 'N';
  const vehicleType = isNewVehicle ? 'new' : 'used';

  const isCPO = !!(!isNewVehicle && qualityLabel?.length && qualityLabel[0].label === 'CPO');
  const vehicleDataType = vehicleType === 'used' && isCPO ? 'cpo' : vehicleType;

  // Vehicle Type
  const dataType = useMemo(() => {
    if (isNonAudi) return 'non-audi';
    return vehicleDataType;
  }, [isNonAudi, vehicleDataType]);

  const listedPricData = (() => {
    if (dealerPrice && dealerPrice > 0) {
      return dealerPrice;
    }
    return modelPriceRetail;
  })();

  const retailPriceData = (() => {
    if (listPrice && listPrice > 0) {
      return listPrice;
    }
    return modelPriceRetail;
  })();

  return (
    <>
      <VendorGroupContainer>
        {ctas?.length > 0 &&
          ctas.map((cta: VendorCTAType, index: any) => {
            const buttonVariant = index === 0 ? 'primary' : 'secondary';
            const key = `${index}${ctas?.length}`;

            return (
              <VendorCtaSelector
                key={key}
                ctaDetails={cta}
                variant={buttonVariant}
                vin={vin}
                vehicleId={vehicleId}
                vehicleType={dataType}
                carType={vehicleType}
                market={market}
                mainDealer={mainDealer}
              />
            );
          })}
        <div
          className="vendorIntegrations_vehicleInfo"
          data-body="null"
          data-make={modelMake || 'null'}
          data-mileage={modelMileage || 'null'}
          data-model-code={modelCode || 'null'}
          data-model-number={modelID || 'null'} // for Audi it returns carline ID for non Audi returns null
          data-model={modelInfo?.genericModel?.text || 'null'}
          data-vin={vin}
          data-listed-price={listedPricData || listPrice || 'null'}
          data-retail-price={retailPriceData || dealerPrice || 'null'}
          data-purchase-price={modelPriceRetail || 'null'} // price key = sale
          data-purchase-list={listPrice || 'null'} // price key = list
          data-msrp={listPrice || '0'}
          data-stock={stockNumber || 'null'}
          data-trim={trimName || 'null'}
          data-type={dataType === 'cpo' ? 'certified' : dataType} // NOT SURE , NEEDS TO BE TESTED
          data-year={modelYear}
        />
      </VendorGroupContainer>
    </>
  );
};

export default VendorCTAsGroup;
